import { RULE_IDS, getRuleMeta } from './rule_ids.js';

const GENERATES = {
  Moc: 'Hoa',
  Hoa: 'Tho',
  Tho: 'Kim',
  Kim: 'Thuy',
  Thuy: 'Moc'
};

const OVERCOMES = {
  Moc: 'Tho',
  Tho: 'Thuy',
  Thuy: 'Hoa',
  Hoa: 'Kim',
  Kim: 'Moc'
};

const CONTROLS = Object.fromEntries(
  Object.entries(OVERCOMES).map(([key, value]) => [value, key])
);

const SEASON_BY_BRANCH = {
  Dan: 'Moc',
  Mao: 'Moc',
  Thin: 'Moc',
  Ty_: 'Hoa',
  Ngo: 'Hoa',
  Mui: 'Hoa',
  Than: 'Kim',
  Dau: 'Kim',
  Tuat: 'Kim',
  Hoi: 'Thuy',
  Ty: 'Thuy',
  Suu: 'Thuy'
};

const getResourceElement = (dayElement) => (
  Object.entries(GENERATES).find(([, value]) => value === dayElement)?.[0]
);

const getController = (dayElement) => CONTROLS[dayElement];
const getCounterForElement = (element) => (
  Object.entries(OVERCOMES).find(([, target]) => target === element)?.[0] || null
);

const findDominantElement = (fiveElements) => {
  if (!fiveElements) return null;
  const entries = Object.entries(fiveElements);
  if (!entries.length) return null;
  const [element, value] = entries.reduce(
    (acc, curr) => (curr[1] > acc[1] ? curr : acc),
    ['', -Infinity]
  );
  const total = entries.reduce((sum, [, v]) => sum + (v || 0), 0) || 0;
  const ratio = total > 0 ? value / total : 0;
  return { element, value, ratio };
};

export const getSeasonElement = (monthBranch) => (
  monthBranch ? SEASON_BY_BRANCH[monthBranch] || null : null
);

export const resolveBodyStrength = (dayElement, monthBranch) => {
  if (!dayElement || !monthBranch) return 'unknown';
  const seasonElement = getSeasonElement(monthBranch);
  if (!seasonElement) return 'unknown';
  if (seasonElement === dayElement) return 'strong';
  if (GENERATES[seasonElement] === dayElement) return 'strong';
  if (OVERCOMES[seasonElement] === dayElement) return 'weak';
  return 'neutral';
};

const buildDungThanByRule = (dayElement, bodyStrength, seasonElement) => {
  if (!dayElement || bodyStrength === 'unknown') return null;

  const resource = getResourceElement(dayElement);
  const controller = getController(dayElement);
  const output = { dung_than: [], hy_than: [], ky_than: [] };

  if (bodyStrength === 'weak') {
    output.dung_than = resource ? [resource] : [];
    output.hy_than = [dayElement];
    output.ky_than = [OVERCOMES[dayElement]];
  } else if (bodyStrength === 'strong') {
    if (dayElement === 'Moc') {
      output.dung_than = ['Kim', 'Hoa'];
    } else if (dayElement === 'Hoa') {
      output.dung_than = ['Thuy'];
    } else if (dayElement === 'Tho') {
      output.dung_than = ['Moc', 'Kim'];
    } else if (dayElement === 'Kim') {
      output.dung_than = ['Hoa'];
    } else if (dayElement === 'Thuy') {
      output.dung_than = ['Tho'];
    }

    output.hy_than = controller ? [controller] : [];
    output.ky_than = [GENERATES[dayElement]];
  } else {
    output.dung_than = seasonElement ? [seasonElement] : [];
    output.hy_than = resource ? [resource] : [];
    output.ky_than = controller ? [controller] : [];
  }

  return output;
};

export function findDungThan(_fiveElements, { dayMasterElement, monthBranch } = {}) {
  const dayElement = dayMasterElement || null;
  const seasonElement = getSeasonElement(monthBranch);
  const bodyStrength = resolveBodyStrength(dayElement, monthBranch);
  const dominant = findDominantElement(_fiveElements);
  let ruleResult = buildDungThanByRule(dayElement, bodyStrength, seasonElement);
  const ruleMeta = getRuleMeta(RULE_IDS.USEFUL_GODS_CORE);

  if (bodyStrength === 'neutral' && dominant?.ratio >= 0.4) {
    const counter = getCounterForElement(dominant.element);
    ruleResult = {
      dung_than: counter ? [counter] : [],
      hy_than: dayElement ? [dayElement] : [],
      ky_than: [dominant.element]
    };
  }
  const reasoning = [
    {
      ruleId: RULE_IDS.USEFUL_GODS_CORE,
      ruleVersion: ruleMeta.ruleVersion,
      explain_vi: ruleMeta.explain_vi
    },
    { factor: 'DayMaster', value: dayElement || 'unknown' },
    { factor: 'SeasonElement', value: seasonElement || 'unknown' },
    { factor: 'BodyStrength', value: bodyStrength },
    dominant ? { factor: 'DominantElement', value: dominant.element, ratio: dominant.ratio } : null
  ].filter(Boolean);

  if (!ruleResult) {
    return {
      pattern: 'RuleBasedCore',
      dung_than: [],
      hy_than: [],
      ky_than: [],
      verified: false,
      ruleId: RULE_IDS.USEFUL_GODS_CORE,
      ruleVersion: ruleMeta.ruleVersion,
      reason: 'Thieu thong tin ngay chu hoac thang',
      explain_vi: ruleMeta.explain_vi,
      inputs: { dayMasterElement: dayElement, monthBranch },
      outputs: {
        roles: { dungThan: [], hyThan: [], kyThan: [] },
        reasoning
      },
      reasoning
    };
  }

  return {
    pattern: 'RuleBasedCore',
    ruleId: RULE_IDS.USEFUL_GODS_CORE,
    ruleVersion: ruleMeta.ruleVersion,
    dung_than: ruleResult.dung_than,
    hy_than: ruleResult.hy_than,
    ky_than: ruleResult.ky_than,
    verified: bodyStrength !== 'unknown',
    reason: `DayMaster=${dayElement}, Season=${seasonElement || 'unknown'}, Body=${bodyStrength}`,
    explain_vi: ruleMeta.explain_vi,
    inputs: {
      dayMasterElement: dayElement,
      monthBranch,
      bodyStrength,
      dominantElement: dominant?.element || null
    },
    outputs: {
      roles: {
        dungThan: ruleResult.dung_than,
        hyThan: ruleResult.hy_than,
        kyThan: ruleResult.ky_than
      },
      reasoning
    },
    reasoning
  };
}
